// src/pages/Dashboard/NotificationsDisplay.jsx
import React, { useState, useEffect } from "react";
import {
  getNotificationsByUser,
  deleteNotificationById,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from "../../services/notificationServices";
import { useNotification } from "./../../contexts/NotificationContext";
import { RiCheckDoubleFill } from "react-icons/ri";
import Button from "../../components/Button/Button";
import ButtonEyeNoRead from "../../components/Button/ButtonEyeNoRead";
import ButtonEyeRead from "../../components/Button/ButtonEyeRead";
import ButtonDelete from "../../components/Button/ButtonDelete";
import { FaBell } from "react-icons/fa";
import { toast } from "react-toastify";
import NotificationCard from "./NotificationCard";
import "./styles.css";

const NotificationsDisplay = ({ userId }) => {
  const [notifications, setNotifications] = useState([]);
  const { unreadCount, setUnreadCount } = useNotification();

  const loadNotifications = async () => {
    if (!userId) {
      console.error("L'ID de l'utilisateur n'est pas trouvé");
      return;
    }
    try {
      const data = await getNotificationsByUser(userId);
      const sorted = (data || []).sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setNotifications(sorted);
    } catch (error) {
      console.error("Erreur lors du chargement des notifications:", error);
      toast.error("Erreur lors du chargement des notifications.");
    }
  };

  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleDeleteNotification = async (notificationId) => {
    try {
      await deleteNotificationById(notificationId);
      await loadNotifications();
      toast.success("Notification supprimée avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression de la notification:", error);
      toast.error("Erreur lors de la suppression de la notification.");
    }
  };

  const toggleReadStatus = async (notificationId) => {
    try {
      const notification = notifications.find((n) => n.id === notificationId);
      if (!notification) {
        console.error("Notification non trouvée");
        return;
      }

      const currentReadStatus = notification.read_notification;

      await markNotificationAsRead(notificationId);

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId
            ? { ...n, read_notification: !n.read_notification }
            : n
        )
      );

      setUnreadCount((prevCount) =>
        currentReadStatus ? prevCount + 1 : Math.max(0, prevCount - 1)
      );
    } catch (error) {
      console.error("Erreur lors du basculement de l'état de lecture:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(userId);
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read_notification: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error(
        "Erreur lors du marquage de toutes les notifications comme lues:",
        error
      );
      toast.error(
        "Erreur lors du marquage de toutes les notifications comme lues."
      );
    }
  };

  return (
    <div className="font-montserrat">
      {/* En-tête */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <FaBell
              className="text-xl text-primary"
              title="Notifications"
            />
            {unreadCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-primary text-white text-[10px] rounded-full px-1.5 py-0.5">
                {unreadCount}
              </span>
            )}
          </div>
          <h3 className="text-sm sm:text-base font-semibold">Notifications</h3>
        </div>

        {notifications.length > 0 && (
          <Button
            hoverLabel="Tout marquer comme lu"
            onClick={handleMarkAllAsRead}
            icon={RiCheckDoubleFill}
            text="Tout lire"
            className="gap-2 text-xs sm:text-sm"
          />
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="text-center py-4 text-sm bg-slate-50 dark:bg-slate-900/80 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
          Pas encore de notifications.
        </div>
      ) : (
        <>
          {/* Desktop : tableau */}
          <div className="hidden sm:block">
            <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-200 dark:bg-slate-800">
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-800 dark:text-slate-100">
                      Date
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-800 dark:text-slate-100">
                      Notification
                    </th>
                    <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-800 dark:text-slate-100">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.map((notification) => (
                    <tr
                      key={notification.id}
                      className={`border-t border-slate-200 dark:border-slate-700 ${
                        notification.read_notification
                          ? "bg-white dark:bg-slate-900"
                          : "bg-primary/5 dark:bg-primary/15"
                      }`}
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-700 dark:text-slate-200">
                        {new Date(notification.created_at).toLocaleDateString(
                          "fr-FR",
                          {
                            year: "2-digit",
                            month: "2-digit",
                            day: "2-digit",
                          }
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-200 max-w-md">
                        <span className="block break-words">
                          {notification.message}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex justify-end gap-1.5">
                          {notification.read_notification ? (
                            <ButtonEyeRead
                              onClick={() =>
                                toggleReadStatus(notification.id)
                              }
                              hoverLabel="Marquer comme non lu"
                            />
                          ) : (
                            <ButtonEyeNoRead
                              onClick={() =>
                                toggleReadStatus(notification.id)
                              }
                              hoverLabel="Marquer comme lu"
                            />
                          )}
                          <ButtonDelete
                            onClick={() =>
                              handleDeleteNotification(notification.id)
                            }
                            hoverLabel="Supprimer"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile : cartes swipeables */}
          <div className="block sm:hidden mt-2">
            {notifications.map((notification) => (
              <NotificationCard
                key={notification.id}
                notification={notification}
                toggleReadStatus={toggleReadStatus}
                handleDeleteNotification={handleDeleteNotification}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationsDisplay;
